import React, { useState, useEffect } from 'react';
import type { Match } from '../hooks/useMatches';
import { X, Search } from 'lucide-react';

interface Props {
  match: Match | null;
  isNew?: boolean;
  onSave: (data: Partial<Match>) => Promise<void>;
  onClose: () => void;
}

export function MatchModal({ match, isNew, onSave, onClose }: Props) {
  const [form, setForm] = useState({
    home_team: '', away_team: '', competition: '', channel: '',
    match_date: new Date().toISOString().split('T')[0], match_time: '21:00', sport: 'football',
    home_logo_url: '', away_logo_url: '',
    phase: '', group_name: '', stadium: '', stadium_city: '',
  });
  const [saving, setSaving] = useState(false);
  const [searchingLogo, setSearchingLogo] = useState<'home' | 'away' | null>(null);

  useEffect(() => {
    if (match) {
      setForm({
        home_team: match.home_team || '', away_team: match.away_team || '',
        competition: match.competition || '', channel: match.channel || '',
        match_date: match.match_date || '', match_time: match.match_time || '21:00',
        sport: match.sport || 'football',
        home_logo_url: match.home_logo_url || '', away_logo_url: match.away_logo_url || '',
        phase: match.phase || '', group_name: match.group_name || '',
        stadium: match.stadium || '', stadium_city: match.stadium_city || '',
      });
    }
  }, [match]);

  const handleSave = async () => {
    if (!form.home_team || !form.away_team || !form.match_date) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        channel: form.channel || null,
        home_logo_url: form.home_logo_url || null,
        away_logo_url: form.away_logo_url || null,
        phase: form.phase || null,
        group_name: form.group_name || null,
        stadium: form.stadium || null,
        stadium_city: form.stadium_city || null,
      });
      onClose();
    } catch {} finally { setSaving(false); }
  };

  const searchLogo = async (side: 'home' | 'away') => {
    const team = side === 'home' ? form.home_team : form.away_team;
    if (!team) return;
    setSearchingLogo(side);
    try {
      const res = await fetch(`https://www.thesportsdb.com/api/v1/json/123/searchteams.php?t=${encodeURIComponent(team)}`);
      const data = await res.json();
      if (data?.teams?.[0]?.strBadge) {
        const url = data.teams[0].strBadge;
        setForm(f => ({ ...f, [side === 'home' ? 'home_logo_url' : 'away_logo_url']: url }));
      }
    } catch {} finally { setSearchingLogo(null); }
  };

  const set = (key: string, val: string) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel slide-in">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent)' }}>
            {isNew ? 'Ajouter un match' : 'Modifier le match'}
          </h2>
          <button onClick={onClose} className="btn-icon"><X size={18} /></button>
        </div>

        {/* Section: Match */}
        <div className="modal-section-title">Match</div>
        <div className="form-grid-2">
          <div className="form-field">
            <label>Équipe domicile</label>
            <input value={form.home_team} onChange={e => set('home_team', e.target.value)} placeholder="Ex: PSG" />
          </div>
          <div className="form-field">
            <label>Équipe extérieur</label>
            <input value={form.away_team} onChange={e => set('away_team', e.target.value)} placeholder="Ex: Arsenal" />
          </div>
        </div>
        <div className="form-grid-2">
          <div className="form-field">
            <label>Compétition</label>
            <input value={form.competition} onChange={e => set('competition', e.target.value)} placeholder="Champions League" />
          </div>
          <div className="form-field">
            <label>Chaîne TV</label>
            <input value={form.channel} onChange={e => set('channel', e.target.value)} placeholder="Canal+" />
          </div>
        </div>
        <div className="form-grid-3">
          <div className="form-field">
            <label>Date</label>
            <input type="date" value={form.match_date} onChange={e => set('match_date', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Heure</label>
            <input type="time" value={form.match_time} onChange={e => set('match_time', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Sport</label>
            <select value={form.sport} onChange={e => set('sport', e.target.value)}>
              <option value="football">Football</option>
              <option value="rugby">Rugby</option>
              <option value="basketball">Basketball</option>
            </select>
          </div>
        </div>

        {/* Section: Logos */}
        <div className="modal-section-title" style={{ marginTop: '20px' }}>Logos</div>
        <div className="form-grid-2">
          <div className="form-field">
            <label>Logo domicile</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {form.home_logo_url && <img src={form.home_logo_url} alt="" style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} onError={e => (e.currentTarget.style.display = 'none')} />}
              <input value={form.home_logo_url} onChange={e => set('home_logo_url', e.target.value)} placeholder="URL..." style={{ flex: 1 }} />
              <button className="btn-icon" onClick={() => searchLogo('home')} disabled={searchingLogo === 'home'} title="Chercher">
                <Search size={14} />
              </button>
            </div>
          </div>
          <div className="form-field">
            <label>Logo extérieur</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {form.away_logo_url && <img src={form.away_logo_url} alt="" style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} onError={e => (e.currentTarget.style.display = 'none')} />}
              <input value={form.away_logo_url} onChange={e => set('away_logo_url', e.target.value)} placeholder="URL..." style={{ flex: 1 }} />
              <button className="btn-icon" onClick={() => searchLogo('away')} disabled={searchingLogo === 'away'} title="Chercher">
                <Search size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Section: Phase CDM */}
        <div className="modal-section-title" style={{ marginTop: '20px' }}>Phase (CDM)</div>
        <div className="form-grid-2">
          <div className="form-field">
            <label>Phase</label>
            <input value={form.phase} onChange={e => set('phase', e.target.value)} placeholder="Groupes / 8es / Quarts..." />
          </div>
          <div className="form-field">
            <label>Groupe</label>
            <input value={form.group_name} onChange={e => set('group_name', e.target.value)} placeholder="Groupe A" />
          </div>
        </div>
        <div className="form-grid-2">
          <div className="form-field">
            <label>Stade</label>
            <input value={form.stadium} onChange={e => set('stadium', e.target.value)} placeholder="Stade de France" />
          </div>
          <div className="form-field">
            <label>Ville</label>
            <input value={form.stadium_city} onChange={e => set('stadium_city', e.target.value)} placeholder="Saint-Denis" />
          </div>
        </div>

        {match && (
          <p style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '12px', fontStyle: 'italic' }}>
            ID: {match.id.substring(0, 8)}...
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-outline" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sauvegarde...' : isNew ? 'Ajouter' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}
